import React from "react";
import { X, Utensils, Users, Calendar, Edit2, Trash2 } from "lucide-react";

export default function TableDetailModal({
  isOpen,
  onClose,
  table,
  onEdit,
  onDelete,
  onStatusChange,
}) {
  if (!isOpen || !table) return null;

  const statusUpper = String(table.status || "").toUpperCase();
  const isAvailable = statusUpper === "AVAILABLE" || statusUpper === "KOSONG";
  const isOccupied = statusUpper === "OCCUPIED" || statusUpper === "TERISI";
  const isReserved = statusUpper === "RESERVED" || statusUpper === "RESERVASI";

  const statusLabel = isAvailable
    ? "Kosong"
    : isOccupied
    ? "Terisi"
    : isReserved
    ? "Reservasi"
    : "Nonaktif";

  const statusColor = isAvailable
    ? { dot: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" }
    : isOccupied
    ? { dot: "bg-rose-500", badge: "bg-rose-50 text-rose-700 ring-1 ring-rose-200" }
    : isReserved
    ? { dot: "bg-amber-500", badge: "bg-amber-50 text-amber-700 ring-1 ring-amber-200" }
    : { dot: "bg-slate-400", badge: "bg-slate-100 text-slate-600 ring-1 ring-slate-200" };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-2xl w-full max-w-md flex flex-col overflow-hidden">

        {/* Header — teal bar matching AddSalesTypeModal */}
        <div className="flex items-center justify-between px-6 py-4 bg-teal-600 text-white rounded-t-2xl">
          <div className="flex items-center gap-2.5">
            <Utensils className="w-4 h-4 opacity-80" />
            <h2 className="text-base font-semibold tracking-tight">Detail Meja</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">

          {/* Table Name Row */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Nama Meja</p>
              <p className="text-2xl font-extrabold text-slate-900">{table.name}</p>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${statusColor.badge}`}>
              <span className={`w-2 h-2 rounded-full ${statusColor.dot} shrink-0`}></span>
              {statusLabel}
            </span>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
              <div className="flex items-center gap-1.5 text-slate-400 mb-1.5">
                <Users className="w-3.5 h-3.5" />
                <span className="text-[10px] font-semibold uppercase tracking-wider">Kapasitas</span>
              </div>
              <p className="text-sm font-bold text-slate-900">{table.capacity || 2} Orang</p>
            </div>

            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
              <div className="flex items-center gap-1.5 text-slate-400 mb-1.5">
                <Calendar className="w-3.5 h-3.5" />
                <span className="text-[10px] font-semibold uppercase tracking-wider">Ditambahkan</span>
              </div>
              <p className="text-sm font-bold text-slate-900">
                {table.created_at
                  ? new Date(table.created_at).toLocaleDateString("id-ID", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "-"}
              </p>
            </div>
          </div>

          {/* Occupation / Reservation info box */}
          {(isOccupied || isReserved) && (
            <div className={`rounded-xl border p-4 space-y-2 ${
              isReserved
                ? "bg-amber-50 border-amber-200"
                : "bg-rose-50 border-rose-200"
            }`}>
              <p className={`text-[11px] font-bold uppercase tracking-wider ${
                isReserved ? "text-amber-600" : "text-rose-600"
              }`}>
                {isReserved ? "Detail Reservasi" : "Detail Penggunaan"}
              </p>
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-xs">{isReserved ? "Dipesan oleh" : "Terisi oleh"}</span>
                  <span className="font-semibold text-slate-900">{table.reserved_by || "Pelanggan Umum"}</span>
                </div>
                {table.notes && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500 text-xs">Catatan / Waktu</span>
                    <span className="font-medium text-slate-700 text-xs">{table.notes}</span>
                  </div>
                )}
              </div>
              {(isOccupied || isReserved) && (
                <button
                  type="button"
                  onClick={() => onStatusChange?.(table.id, "AVAILABLE")}
                  className="mt-2 w-full py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors">
                  Kosongkan Meja
                </button>
              )}
            </div>
          )}

          {/* Notes (only when available) */}
          {table.notes && isAvailable && (
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Catatan Meja</p>
              <p className="text-sm text-slate-700">{table.notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 rounded-b-2xl flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              onClose();
              onDelete?.(table);
            }}
            className="px-4 py-2 bg-white ring-1 ring-rose-200 text-rose-600 rounded-lg hover:bg-rose-50 font-medium text-sm transition-colors flex items-center gap-1.5">
            <Trash2 className="w-3.5 h-3.5" />
            Hapus
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                onClose();
                onEdit?.(table);
              }}
              className="px-4 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors flex items-center gap-1.5">
              <Edit2 className="w-3.5 h-3.5" />
              Edit
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors shadow-sm shadow-teal-600/20">
              Tutup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
